import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../slices/store";
interface LineItemDropdownProps {
  lineItems: any[];
  selectedItems: any[];
  handleCheckboxChange: (itemId: any) => void;
  handleSelectAllChange: (allItemIds: any[]) => void;
  module?: string;
  disabled?: boolean;
}

const LineItemDropdown: React.FC<LineItemDropdownProps> = ({ lineItems, selectedItems, handleCheckboxChange, handleSelectAllChange, module, disabled = false }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filters = useSelector((state: RootState) => state.filters);

  const isAllSelected = useMemo(() => {
    if (lineItems.length === 0 || selectedItems.length !== lineItems.length) {
      return false;
    }

    // Convert both arrays to strings for consistent comparison
    const lineItemIds = lineItems.map(item => String(item.Id)).sort();
    const selectedItemIds = selectedItems.map(String).sort();

    // Check if arrays contain the same values
    return lineItemIds.every((id, index) => id === selectedItemIds[index]);
  }, [lineItems, selectedItems]);

  // When toggling
  const toggleDropdown = () => {
    if (disabled) return; // Prevent opening when disabled
    if (!dropdownOpen) {
      const event = new CustomEvent("dropdownOpened", { detail: dropdownRef.current });
      window.dispatchEvent(event);
    }
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);



  const handleSelectAll = () => {
    if (disabled) return; // Prevent action when disabled
    if (isAllSelected) {
      handleSelectAllChange([]);
    } else {
      const allItemIds = lineItems.map(item => item.Id);
      handleSelectAllChange(allItemIds);
    }
  };


  const isChecked = (itemId: any) => selectedItems.map(String).includes(String(itemId));

  return (
    <div ref={dropdownRef} style={{ position: "relative", whiteSpace: "nowrap" }}>
      <div
        onClick={toggleDropdown}
        className="line-item-dropdown custom-dropdown-toggle react-select__control"
        style={{
          background: disabled ? "var(--bs-gray-100, #f5f5f5)" : "var(--bs-white, #fff)",
          border: "1px solid var(--bs-border-color, #ced4da)",
          height: "38px",
          borderRadius: "4px",
          padding: "8px 12px",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          display: "flex",
          alignItems: "center",
          minHeight: "38px",
        }}
      >
        {(module === 'report-page' ? selectedItems.length > 0 : filters.selectedLineItems.length > 0)
          ? `${selectedItems.length} item(s) selected`
          : "Select Line Items"}

      </div>

      {dropdownOpen && (
        <ul
          className="line-item-list custom-dropdown-list"
        >
          {lineItems.length === 0 ? (
            <li style={{ padding: "10px", textAlign: "center", color: "#888" }} className="line-item-empty custom-dropdown-empty">
              No Options
            </li>
          ) : (
            <>
              {/* Add Select All option */}
              <li className="line-item-option custom-dropdown-option" style={{paddingTop:"10px"}}>
                <label style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={disabled}
                    style={{ marginRight: "10px",marginTop:"-1px" }}
                  />
                  Select All
                </label>
              </li>
              {/* Existing line items */}
              {lineItems.map((item) => (
                <li key={item.Id} className="line-item-option custom-dropdown-option">
                  <label className="line-item-text" title={item.Line_Item_Name__c}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={item.Id}
                      checked={isChecked(item.Id)}
                      onChange={() => !disabled && handleCheckboxChange(item.Id)}
                      disabled={disabled}
                      style={{ marginRight: "10px" }}
                    />
                    {item.Line_Item_Name__c}
                  </label>
                </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  );
};

export default LineItemDropdown;
